import { useState, useEffect, useRef, useCallback } from 'react';
import { 
  getSettings, 
  updateSettings,
  getEmailTemplates, 
  updateEmailTemplate, 
  getGoogleCalendarStatus, 
  disconnectGoogleCalendar 
} from '../utils/api';
import { playNotificationSound } from '../utils/notifications';
import NotificationToast from '../components/NotificationToast';
import DatabaseCleanupModal from '../components/DatabaseCleanupModal';
import Error500 from './errors/Error500';
import './AdminPages.css';
import './AdminSettingsPage.css';

const sanitizeSettings = (incoming) => {
  if (!incoming) return {};
  const next = { ...incoming };
  delete next.google_calendar_sync_interval;
  delete next.remember_login_token_hash;
  delete next.remember_login_token_expires_at;
  return next;
};

const AdminSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [serverError, setServerError] = useState(false);
  const [settings, setSettings] = useState({});
  const [workingHours, setWorkingHours] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [templateSubject, setTemplateSubject] = useState('');
  const [templateBody, setTemplateBody] = useState('');
  const [templateHtml, setTemplateHtml] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [activeTab, setActiveTab] = useState('edit');
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
  const [showTestNotification, setShowTestNotification] = useState(false);
  const googleCalendarTimerRef = useRef(null);
  const [showCleanupModal, setShowCleanupModal] = useState(false);

  const dayNames = ['Nedeľa', 'Pondelok', 'Utorok', 'Streda', 'Štvrtok', 'Piatok', 'Sobota'];

  const testNotificationSound = () => {
    playNotificationSound(settings.notification_sound || 'default');
    
    setShowTestNotification(true);
    setTimeout(() => {
      setShowTestNotification(false);
    }, 5000);
  };

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setServerError(false);
      const response = await getSettings();
      const data = response.data.data || response.data;
      
      setSettings(sanitizeSettings(data.settings || {}));
      setWorkingHours(data.working_hours || []);
      setError('');
    } catch (err) {
      console.error('Failed to load settings:', err);
      if (!err.response || err.response.status >= 500 || err.code === 'ERR_NETWORK') {
        setServerError(true);
      } else {
        setError('Nepodarilo sa načítať nastavenia');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEmailTemplates = useCallback(async () => {
    try {
      const response = await getEmailTemplates();
      const templates = response.data.data || response.data || [];
      setEmailTemplates(templates);
    } catch (err) {
      console.error('Failed to load email templates:', err);
      if (!err.response || err.response.status >= 500 || err.code === 'ERR_NETWORK') {
        setServerError(true);
      }
    }
  }, []);

  const loadGoogleCalendarStatus = useCallback(async () => {
    try {
      const response = await getGoogleCalendarStatus();
      const isConnected = response.data.connected || false;
      setGoogleCalendarConnected(isConnected);
      
      if (isConnected && googleCalendarTimerRef.current) {
        clearTimeout(googleCalendarTimerRef.current);
        googleCalendarTimerRef.current = null;
      }
    } catch (err) {
      console.error('Failed to load Google Calendar status:', err);
      if (!err.response || err.response.status >= 500 || err.code === 'ERR_NETWORK') {
        setServerError(true);
      }
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadEmailTemplates();
    loadGoogleCalendarStatus();
  }, [loadSettings, loadEmailTemplates, loadGoogleCalendarStatus]);

  useEffect(() => {
    let intervalId;
    
    if (settings.google_calendar_sync_enabled && !googleCalendarConnected && googleCalendarTimerRef.current) {
      intervalId = setInterval(() => {
        loadGoogleCalendarStatus();
      }, 5000);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [settings.google_calendar_sync_enabled, googleCalendarConnected, loadGoogleCalendarStatus]);

  useEffect(() => {
    return () => {
      if (googleCalendarTimerRef.current) {
        clearTimeout(googleCalendarTimerRef.current);
      }
    };
  }, []);


  const handleSettingChange = async (key, value) => {
    if (key === 'google_calendar_sync_enabled') {
      if (value === true && !googleCalendarConnected) {
        if (googleCalendarTimerRef.current) {
          clearTimeout(googleCalendarTimerRef.current);
        }
        
        googleCalendarTimerRef.current = setTimeout(async () => {
          if (!googleCalendarConnected) {
            const newSettings = {
              ...settings,
              google_calendar_sync_enabled: false
            };
            setSettings(newSettings);
            try {
              await updateSettings({ settings: newSettings, working_hours: workingHours });
              setTimeout(() => setError(''), 5000);
            } catch (err) {
              console.error('Failed to auto-disable sync:', err);
            }
          }
        }, 30000);
      }
    }
    
    const newSettings = sanitizeSettings({
      ...settings,
      [key]: value
    });
    setSettings(newSettings);
    try {
      await updateSettings({ settings: newSettings, working_hours: workingHours });
    } catch (err) {
      setError(err.response?.data?.message || 'Nepodarilo sa uložiť nastavenia');
    }
  };

  const handleWorkingHourChange = async (dayIndex, field, value) => {
    const newWorkingHours = workingHours.map(wh => {
      if (wh.den_v_tyzdni === dayIndex) {
        return { ...wh, [field]: value };
      }
      return wh;
    });
    setWorkingHours(newWorkingHours);
    try {
      await updateSettings({ settings: sanitizeSettings(settings), working_hours: newWorkingHours });
    } catch (err) {
      setError(err.response?.data?.message || 'Nepodarilo sa uložiť nastavenia');
    }
  };

  const handleOpenTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateSubject(template.predmet || '');
    setTemplateBody(template.telo_text || '');
    setTemplateHtml(template.telo_html || '');
    setActiveTab('split');
    setShowTemplateModal(true);
  };

  const handleCloseTemplateModal = () => {
    setIsModalClosing(true);
    setTimeout(() => {
      setShowTemplateModal(false);
      setIsModalClosing(false);
      setEditingTemplate(null);
      setTemplateSubject('');
      setTemplateBody('');
      setTemplateHtml('');
    }, 300);
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    
    try {
      setSavingTemplate(true);
      setError('');
      
      await updateEmailTemplate(editingTemplate.id, {
        predmet: templateSubject,
        telo_text: templateBody,
        telo_html: templateHtml
      });
      
      await loadEmailTemplates();
      handleCloseTemplateModal();
    } catch (err) {
      console.error('Failed to save template:', err);
      setError(err.response?.data?.message || 'Nepodarilo sa uložiť šablónu');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleGoogleCalendarConnect = async () => {
    if (googleCalendarConnected) {
      try {
        await disconnectGoogleCalendar();
        setGoogleCalendarConnected(false);
        if (googleCalendarTimerRef.current) {
          clearTimeout(googleCalendarTimerRef.current);
          googleCalendarTimerRef.current = null;
        }
      } catch (err) {
        console.error('Failed to disconnect Google Calendar:', err);
        setError('Nepodarilo sa odpojiť Google Calendar');
      }
    } else {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      window.open(`${API_URL}/api/admin/google-calendar/connect`, '_blank');
    }
  };

  // Temporary file with handler functions - add these to AdminSettingsPage.jsx before "if (serverError)"

const handleBackupDatabase = async () => {
  try {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    const response = await fetch(`${API_URL}/api/admin/database/backup`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Backup failed');
    }

    // Download the file
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `backup_${new Date().toISOString().split('T')[0]}.sql`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    
  } catch (err) {
    console.error('Backup failed:', err);
    setError('Nepodarilo sa vytvoriť zálohu databázy');
    setTimeout(() => setError(''), 3000);
  }
};

const handleCleanupComplete = (deletedCount) => {
    setShowCleanupModal(false);
  };


  if (serverError) {
    return <Error500 />;
  }

  if (loading) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <div>
            <h1>Nastavenia systému</h1>
            <p className="text-muted">Konfigurácia rezervačného systému</p>
          </div>
        </div>

        {/* Skeleton Grid */}
        <div className="skeleton-grid">
          <div className="skeleton-block skeleton-block-half">
            <div className="skeleton-header">
              <div className="skeleton-title"></div>
              <div className="skeleton-toggle"></div>
            </div>
            <div className="skeleton-text"></div>
            <div className="skeleton-text"></div>
          </div>
          <div className="skeleton-block skeleton-block-half">
            <div className="skeleton-header">
              <div className="skeleton-title"></div>
              <div className="skeleton-toggle"></div>
            </div>
            <div className="skeleton-text"></div>
            <div className="skeleton-text"></div>
          </div>
          <div className="skeleton-block skeleton-block-third">
            <div className="skeleton-header">
              <div className="skeleton-title"></div>
            </div>
            <div className="skeleton-text"></div>
          </div>
          <div className="skeleton-block skeleton-block-third">
            <div className="skeleton-header">
              <div className="skeleton-title"></div>
            </div>
            <div className="skeleton-text"></div>
          </div>
          <div className="skeleton-block skeleton-block-third">
            <div className="skeleton-header">
              <div className="skeleton-title"></div>
            </div>
            <div className="skeleton-text"></div>
          </div>
          <div className="skeleton-block skeleton-block-full" style={{height: '200px'}}>
            <div className="skeleton-header">
              <div className="skeleton-title"></div>
            </div>
            <div className="skeleton-text"></div>
            <div className="skeleton-text"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page settings-page">
      <div className="page-header">
        <div>
          <h1>Nastavenia systému</h1>
          <p className="text-muted">Konfigurácia rezervačného systému</p>
        </div>
        <div className="header-actions"></div>
      </div>

      {error && (
        <div className="alert alert-error mb-3">
          {error}
        </div>
      )}

      {/* {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )} */}

      <div className="settings-bento-container">
        {/* Bento Grid Layout */}
        <div className="bento-grid-modern">
          {/* Buffer Time Between Reservations */}
          <div className="bento-card bento-col-3">
            <div className="bento-header">
              <div className="bento-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div className="bento-title-group">
                <h3 className="bento-title">Časová prestávka</h3>
                <p className="bento-subtitle">Časový odstup medzi rezerváciami</p>
              </div>
            </div>
            <div className="bento-content">
              <div className="input-with-unit">
                <input
                  type="number"
                  className="input-field"
                  min="0"
                  max="120"
                  value={settings.reservation_buffer_minutes ?? 15}
                  onChange={(e) => handleSettingChange('reservation_buffer_minutes', parseInt(e.target.value) || 0)}
                  placeholder="15"
                />
                <span className="input-unit">minút</span>
              </div>
            </div>
          </div>

          {/* Dashboard & Interface - 2 columns compact */}
          <div className="bento-card bento-col-3">
            <div className="bento-header">
              <div className="bento-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                </svg>
              </div>
              <div className="bento-title-group">
                <h3 className="bento-title">Auto-refresh údajov</h3>
                <p className="bento-subtitle">Aktualizácia údajov na dashboarde</p>
              </div>
            </div>
            <div className="bento-content">
              <select
                className="input-field"
                value={settings.dashboard_refresh_interval || 10}
                onChange={(e) => handleSettingChange('dashboard_refresh_interval', parseInt(e.target.value))}
              >
                <option value="2">2 sekundy</option>
                <option value="5">5 sekúnd</option>
                <option value="10">10 sekúnd</option>
              </select>
            </div>
          </div>

          <div className="bento-card bento-col-3">
            <div className="bento-header">
              <div className="bento-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              </div>
              <div className="bento-title-group">
                <h3 className="bento-title">Predvolené zobrazenie rezervácií</h3>
                <p className="bento-subtitle">Nastavenie predvoleného zobrazenia</p>
              </div>
            </div>
            <div className="bento-content bento-content-grid">
              <select
                className="input-field"
                value={settings.default_reservations_view || 'table'}
                onChange={(e) => handleSettingChange('default_reservations_view', e.target.value)}
              >
                <option value="table">Tabuľka</option>
                <option value="calendar">Kalendár</option>
              </select>
              {settings.default_reservations_view === 'calendar' && (
                <select
                  className="input-field"
                  value={settings.default_calendar_view || 'timeGridDay'}
                  onChange={(e) => handleSettingChange('default_calendar_view', e.target.value)}
                >
                  <option value="timeGridDay">Deň</option>
                  <option value="timeGridWeek">Týždeň</option>
                  <option value="listWeek">Agenda</option>
                </select>
              )}
            </div>
          </div>

          <div className="bento-card bento-col-3">
            <div className="bento-header">
              <div className="bento-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div className="bento-title-group">
                <h3 className="bento-title">Uzávierka úprav rezervácií</h3>
                <p className="bento-subtitle">Lehota na zmenu/zrušenie</p>
              </div>
            </div>
            <div className="bento-content">
              <div className="input-with-unit">
                <input
                  type="number"
                  className="input-field"
                  min="0"
                  max="168"
                  value={settings.reservation_edit_deadline_hours ?? 24}
                  onChange={(e) => handleSettingChange('reservation_edit_deadline_hours', parseInt(e.target.value) || 0)}
                  placeholder="24"
                />
                <span className="input-unit">hodín</span>
              </div>
            </div>
          </div>

          {/* Time Slot Interval */}
          <div className="bento-card bento-col-3">
            <div className="bento-header">
              <div className="bento-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                  <line x1="10" y1="14" x2="10" y2="17"/>
                  <line x1="14" y1="14" x2="14" y2="17"/>
                </svg>
              </div>
              <div className="bento-title-group">
                <h3 className="bento-title">Interval medzi časovými slotmi</h3>
                <p className="bento-subtitle">Časový odstup medzi dostupnými termínmi</p>
              </div>
            </div>
            <div className="bento-content">
              <select
                className="input-field"
                value={settings.time_slot_interval_minutes || 30}
                onChange={(e) => handleSettingChange('time_slot_interval_minutes', parseInt(e.target.value))}
              >
                <option value="5">5 minút</option>
                <option value="10">10 minút</option>
                <option value="15">15 minút</option>
                <option value="30">30 minút</option>
                <option value="45">45 minút</option>
              </select>
            </div>
          </div>

          <div className="bento-card bento-col-3">
            <div className="bento-header">
              <div className="bento-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
              </div>
              <div className="bento-title-group">
                <h3 className="bento-title">Desktop notifikácie</h3>
                <p className="bento-subtitle">Upozornenia v prehliadači</p>
              </div>
              <label className="toggle-switch">
                <input 
                  type="checkbox" 
                  checked={!!settings.desktop_notifications_enabled}
                  onChange={(e) => handleSettingChange('desktop_notifications_enabled', e.target.checked ? 1 : 0)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <div className="bento-content">
              <div style={{ display: 'flex', gap: '1rem' }}>
                <select
                  id="notification_sound"
                  className="input-field"
                  style={{ flex: 1 }}
                  value={settings.notification_sound || 'default'}
                  onChange={(e) => handleSettingChange('notification_sound', e.target.value)}
                  disabled={!settings.desktop_notifications_enabled}
                >
                  <option value="default">Default</option>
                  <option value="chime">Zvonček</option>
                  <option value="bell">Zvon</option>
                  <option value="ping">Ping</option>
                  <option value="alert">Alert</option>
                </select>
              <button 
                className="btn btn-primary"
                onClick={testNotificationSound}
                disabled={!settings.desktop_notifications_enabled}
              >
                Prehrať notifikáciu
              </button>
              </div>
            </div>
          </div>

          <div className="bento-card bento-col-3">
            <div className="bento-header">
              <div className="bento-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <div className="bento-title-group">
                <h3 className="bento-title">Maximálny predstih</h3>
                <p className="bento-subtitle">Najdlhší čas pred termínom, kedy je možné rezervovať</p>
              </div>
            </div>
            <div className="bento-content">
              <div className="input-with-unit">
                <input
                  type="number"
                  className="input-field"
                  min="0"
                  max="365"
                  value={settings.max_advance_days ?? 60}
                  onChange={(e) => handleSettingChange('max_advance_days', parseInt(e.target.value) || 0)}
                />
                <span className="input-unit">dní</span>
              </div>
            </div>
          </div>

          {/* Time Settings - 3 compact columns */}
          <div className="bento-card bento-col-3">
            <div className="bento-header">
              <div className="bento-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div className="bento-title-group">
                <h3 className="bento-title">Minimálny predstih</h3>
                <p className="bento-subtitle">Najkratší čas pred termínom, kedy je možné rezervovať</p>
              </div>
            </div>
            <div className="bento-content">
              <div className="input-with-unit">
                <input
                  type="number"
                  className="input-field"
                  min="0"
                  value={settings.min_advance_days ?? 1}
                  onChange={(e) => handleSettingChange('min_advance_days', parseInt(e.target.value) || 0)}
                />
                <span className="input-unit">dní</span>
              </div>
            </div>
          </div>

          <div className="bento-card bento-col-3">
            <div className="bento-header">
              <div className="bento-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div className="bento-title-group">
                <h3 className="bento-title">Časový rozsah kalendára</h3>
                <p className="bento-subtitle">Zobrazenie časového rozsahu kalendára</p>
              </div>
            </div>
            <div className="bento-content bento-content-grid">
              <input
                type="time"
                className="input-field"
                value={settings.calendar_slot_min_time || '07:00'}
                onChange={(e) => handleSettingChange('calendar_slot_min_time', e.target.value)}
              />
              <input
                type="time"
                className="input-field"
                value={settings.calendar_slot_max_time || '20:00'}
                onChange={(e) => handleSettingChange('calendar_slot_max_time', e.target.value)}
              />
            </div>
          </div>

          <div className="bento-card bento-col-3">
            <div className="bento-header">
              <div className="bento-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5"/>
                </svg>
              </div>
              <div className="bento-title-group">
                <h3 className="bento-title">Google Calendar</h3>
                <p className="bento-subtitle">Synchronizácia s Google kalendárom</p>
              </div>
              <label className="toggle-switch">
                <input 
                  type="checkbox" 
                  checked={settings.google_calendar_sync_enabled || false}
                  onChange={(e) => handleSettingChange('google_calendar_sync_enabled', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <div className="bento-content">
              <button
                className={`btn btn-connect ${googleCalendarConnected ? 'btn-danger' : ''}`}
                onClick={handleGoogleCalendarConnect}
                disabled={!settings.google_calendar_sync_enabled}
                title={googleCalendarConnected ? 'Odpojiť Google kalendár' : 'Pripojiť Google kalendár'}
                style={{width: '100%', justifyContent: 'center'}}
              >
                {googleCalendarConnected ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                      <line x1="8" y1="2" x2="8" y2="5"/>
                      <line x1="2" y1="8" x2="5" y2="8"/>
                      <line x1="16" y1="19" x2="16" y2="22"/>
                      <line x1="19" y1="16" x2="22" y2="16"/>
                    </svg>
                    Odpojiť Google Kalendár
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                    </svg>
                    Pripojiť Google Kalendár
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Database Backup */}
          <div className="bento-card bento-col-3">
            <div className="bento-header">
              <div className="bento-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </div>
              <div className="bento-title-group">
                <h3 className="bento-title">Zálohovanie databázy</h3>
                <p className="bento-subtitle">Stiahnutie SQL dumpu pre zálohovanie dát</p>
              </div>
            </div>
            <div className="bento-content">
              <button 
                className="btn btn-primary"
                style={{width: '100%'}}
                onClick={handleBackupDatabase}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Zálohovať databázu
              </button>
            </div>
          </div>

          {/* Database Cleanup */}
          <div className="bento-card bento-col-3">
            <div className="bento-header">
              <div className="bento-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  <line x1="10" y1="11" x2="10" y2="17"/>
                  <line x1="14" y1="11" x2="14" y2="17"/>
                </svg>
              </div>
              <div className="bento-title-group">
                <h3 className="bento-title">Čistenie záznamov</h3>
                <p className="bento-subtitle">Vymazanie starých údajov</p>
              </div>
            </div>
            <div className="bento-content">
              <button 
                className="btn btn-danger"
                style={{width: '100%'}}
                onClick={() => setShowCleanupModal(true)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  <line x1="10" y1="11" x2="10" y2="17"/>
                  <line x1="14" y1="11" x2="14" y2="17"/>
                </svg>
                Zvoliť mazanie záznamov
              </button>
            </div>
          </div>

          {/* Working Hours - Full width table */}
          <div className="bento-card bento-col-6 bento-card-table">
            <div className="bento-header">
              <div className="bento-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              </div>
              <div className="bento-title-group">
                <h3 className="bento-title">Pracovná doba</h3>
                <p className="bento-subtitle">Možnosť nastaviť pracovnú dobu pre jednotlivé dni</p>
              </div>
            </div>
            <div className="bento-content">
              <table className="working-hours-table">
                <thead>
                  <tr>
                    <th>Deň</th>
                    <th>Voľno</th>
                    <th>Od</th>
                    <th>Do</th>
                  </tr>
                </thead>
                <tbody>
                  {workingHours.map((day) => (
                    <tr key={day.den_v_tyzdni} className={day.zatvorene ? 'day-closed' : ''}>
                      <td className="day-name">{dayNames[day.den_v_tyzdni]}</td>
                      <td>
                        <input
                          type="checkbox"
                          className="checkbox-input"
                          checked={day.zatvorene || false}
                          onChange={(e) => handleWorkingHourChange(day.den_v_tyzdni, 'zatvorene', e.target.checked)}
                        />
                      </td>
                      <td>
                        <input
                          type="time"
                          className="input-field-sm"
                          value={day.cas_od || ''}
                          disabled={day.zatvorene}
                          onChange={(e) => handleWorkingHourChange(day.den_v_tyzdni, 'cas_od', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="time"
                          className="input-field-sm"
                          value={day.cas_do || ''}
                          disabled={day.zatvorene}
                          onChange={(e) => handleWorkingHourChange(day.den_v_tyzdni, 'cas_do', e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bento-card bento-col-3">
            <div className="bento-header">
              <div className="bento-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
              </div>
              <div className="bento-title-group">
                <h3 className="bento-title">Kontaktné údaje</h3>
                <p className="bento-subtitle">Údaje zobrazené na verejnej stránke</p>
              </div>
            </div>
            <div className="bento-content">
              <div className="input-group">
                <label htmlFor="telefon">Telefón</label>
                <input
                  id="telefon"
                  type="tel"
                  className="input-field"
                  value={settings.company_phone || ''}
                  onChange={(e) => handleSettingChange('company_phone', e.target.value)}
                  placeholder="+421 XXX XXX XXX"
                />
              </div>
              <div className="input-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  className="input-field"
                  value={settings.company_email || ''}
                  onChange={(e) => handleSettingChange('company_email', e.target.value)}
                  placeholder="info@firma.sk"
                />
              </div>
              <div className="input-group">
                <label htmlFor="adresa">Adresa</label>
                <textarea
                  id="adresa"
                  className="input-field textarea-address"
                  rows={6}
                  value={settings.company_address || ''}
                  onChange={(e) => handleSettingChange('company_address', e.target.value)}
                  placeholder="Ulica 123, Mesto, PSČ"
                />
              </div>
            </div>
          </div>

          {/* Email Templates */}
          <div className="bento-card bento-col-3">
            <div className="bento-header">
              <div className="bento-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
              </div>
              <div className="bento-title-group">
                <h3 className="bento-title">Email šablóny</h3>
                <p className="bento-subtitle">Nastavenie emailov</p>
              </div>
            </div>
            <div className="bento-content">
              <div className="template-list">
                {emailTemplates.map((template) => (
                  <button
                    key={template.id}
                    className="template-item"
                    onClick={() => handleOpenTemplate(template)}
                  >
                    <div className="template-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                    </div>
                    <div className="template-info">
                      <div className="template-name">{template.nazov}</div>
                      <div className="template-desc">{template.predmet}</div>
                    </div>
                    <div className="template-arrow">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className={`modal-overlay ${isModalClosing ? 'closing' : ''}`} onClick={handleCloseTemplateModal}>
          <div className={`modal-content template-modal template-modal-split ${isModalClosing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-content">
                <h2>Upraviť email šablónu</h2>
                {editingTemplate && (
                  <span className="template-badge">{editingTemplate.typ}</span>
                )}
              </div>
              <button className="modal-close" onClick={handleCloseTemplateModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            
            <div className="modal-tabs">
              <button 
                className={`modal-tab ${activeTab === 'split' ? 'active' : ''}`}
                onClick={() => setActiveTab('split')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="18"/>
                  <rect x="14" y="3" width="7" height="18"/>
                </svg>
                Kód & Náhľad
              </button>
              <button 
                className={`modal-tab ${activeTab === 'fullscreen' ? 'active' : ''}`}
                onClick={() => setActiveTab('fullscreen')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                Iba Náhľad
              </button>
            </div>

            <div className="modal-body template-modal-body">
              {editingTemplate && (
                <>
                  {activeTab === 'split' ? (
                    <div className="template-split-view">
                      <div className="template-editor-panel">
                        <div className="form-group mb-0">
                          <input
                            type="text"
                            className="input-field"
                            value={templateSubject}
                            onChange={(e) => setTemplateSubject(e.target.value)}
                            placeholder="Napíšte predmet emailu..."
                          />
                        </div>

                        <div className="form-group">
                          <textarea
                            className="input-field template-code-textarea"
                            rows="20"
                            value={templateHtml}
                            onChange={(e) => setTemplateHtml(e.target.value)}
                            placeholder="<html>...</html>"
                            spellCheck="false"
                          />
                          <div className="template-variables">
                            <div className="variables-title">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="12" y1="16" x2="12" y2="12"/>
                                <line x1="12" y1="8" x2="12.01" y2="8"/>
                              </svg>
                              Dostupné premenné:
                            </div>
                            <div className="variables-list">
                              <code>{`{customer_name}`}</code>
                              <code>{`{date}`}</code>
                              <code>{`{time}`}</code>
                              <code>{`{service_name}`}</code>
                              <code>{`{price}`}</code>
                              <code>{`{edit_url}`}</code>
                              <code>{`{cancel_url}`}</code>
                              <code>{`{website_url}`}</code>
                              <code>{`{company_email}`}</code>
                              <code>{`{reservation_id}`}</code>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="template-preview-panel">
                        <div className="template-preview-iframe-container">
                          <iframe 
                            srcDoc={templateHtml}
                            title="Email Preview"
                            className="template-preview-iframe"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="template-fullscreen-preview">
                      <iframe 
                        srcDoc={templateHtml}
                        title="Email Preview Fullscreen"
                        className="template-preview-iframe-full"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseTemplateModal}>
                Zrušiť
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleSaveTemplate}
                disabled={savingTemplate}
              >
                {savingTemplate ? 'Ukladám...' : 'Uložiť'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* In-App Test Notification */}
      <NotificationToast
        show={showTestNotification}
        title="Test notifikácie"
        message={`Zvuk: ${settings.notification_sound || 'default'}`}
        onClose={() => setShowTestNotification(false)}
      />

      {/* Database Cleanup Modal */}
      <DatabaseCleanupModal
        isOpen={showCleanupModal}
        onClose={() => setShowCleanupModal(false)}
        onDelete={handleCleanupComplete}
      />
    </div>
  );
};

export default AdminSettingsPage;
